import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import DataUsageRoundedIcon from "@mui/icons-material/DataUsageRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import InsertChartRoundedIcon from "@mui/icons-material/InsertChartRounded";
import { useTranslation } from "react-i18next";
import itemControlService from "../../../services/ItemControlService";
import saleOrderServices from "../../../services/SalesOrderService";
import customerServices from "../../../services/CustomerService";
import { formatCodDate } from "../../../utils/codUtils";
import { inDateRange, ReportDateRange } from "../../../utils/codReports";
import { exportExcelWorkbook, reportFileStamp } from "../../../utils/codReportExcel";
import {
  buildControlRows,
  buildCustomerAccessRows,
  buildSiteRows,
  buildStockIndex,
  buildUncontrolledProducts,
  ControlLogicName,
  ItemControlRecord,
  normalizeItemControl,
  normalizeStockProduct,
  StockProduct,
} from "../../../utils/opsReports";
import { showToast } from "../../component/common/toastNotification";
import PageHeader from "../../../components/ui/PageHeader";
import CodStatCard from "../../../components/ui/CodStatCard";
import CodInitialsAvatar from "../../../components/ui/CodInitialsAvatar";
import EmptyState from "../../../components/ui/EmptyState";
import TableSkeleton from "../../../components/ui/TableSkeleton";
import CodTabBar from "../codSalesRep/CodTabBar";
import { WhmChartsSection } from "./OpsReportCharts";
import { ReportToolbar } from "../codSalesRep/CodReportParts";
import { NJIM, pageShellSx } from "../codSalesRep/codStyles";

type View = "controls" | "site" | "customers" | "uncontrolled" | "charts";

const panelSx = {
  borderRadius: "16px",
  border: `1px solid ${NJIM.border}`,
  background: NJIM.white,
  boxShadow: NJIM.elevation,
  p: { xs: 2, md: 3 },
} as const;

const rowSx = {
  display: "grid",
  alignItems: "center",
  gap: { xs: 1, sm: 2 },
  px: { xs: 1.5, md: 2 },
  py: 1.5,
  borderRadius: "14px",
  bgcolor: NJIM.white,
  border: `1px solid ${NJIM.border}`,
} as const;

const LOGIC_TONE: Record<ControlLogicName, { color: string; bg: string }> = {
  None: { color: NJIM.textSecondary, bg: NJIM.muted },
  Consumption: { color: NJIM.warning, bg: NJIM.warningWash },
  Customer: { color: NJIM.info, bg: NJIM.mintWash },
};

const asArray = (data: unknown): Record<string, unknown>[] => {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  const items = (data as { items?: unknown })?.items;
  return Array.isArray(items) ? (items as Record<string, unknown>[]) : [];
};

const WhmReportsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [controls, setControls] = useState<ItemControlRecord[]>([]);
  const [products, setProducts] = useState<StockProduct[]>([]);
  const [customerNames, setCustomerNames] = useState<Record<string, string>>({});
  const [range, setRange] = useState<ReportDateRange>({ from: "", to: "" });
  const [search, setSearch] = useState("");
  const [onlyRestricted, setOnlyRestricted] = useState(false);
  const [view, setView] = useState<View>("controls");
  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ctrlRes, prodRes] = await Promise.allSettled([
        itemControlService.getAll(),
        saleOrderServices.getAllProducts(5000),
      ]);
      if (ctrlRes.status === "fulfilled") {
        setControls(asArray(ctrlRes.value?.data).map(normalizeItemControl));
      } else {
        showToast(t("opsReports.loadFailed"), "error");
      }
      if (prodRes.status === "fulfilled") {
        setProducts(asArray(prodRes.value?.data).map(normalizeStockProduct));
      }
      // Customer names for authorised-customer lists (same sources as the Item Control page).
      let customers: Record<string, unknown>[] = [];
      try {
        customers = asArray((await customerServices.getAllCustomersUnpaged())?.data);
      } catch {
        /* fall back below */
      }
      if (customers.length === 0) {
        try {
          customers = asArray((await customerServices.getAllCustomers(5000))?.data);
        } catch {
          /* names fall back to ids */
        }
      }
      const names: Record<string, string> = {};
      customers.forEach((c) => {
        const id = String(c.id ?? c.Id ?? "");
        const name = String(c.customerName ?? c.CustomerName ?? c.title ?? c.Title ?? "").trim();
        const code = String(c.customerCode ?? c.CustomerCode ?? "").trim();
        if (id) names[id] = name ? (code ? `${name} (${code})` : name) : code || id;
      });
      setCustomerNames(names);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const customerName = useCallback((id: string) => customerNames[id] || id, [customerNames]);
  const logicLabel = useCallback(
    (logic: ControlLogicName) =>
      logic === "Customer"
        ? t("opsReports.logicCustomer")
        : logic === "Consumption"
          ? t("opsReports.logicConsumption")
          : t("opsReports.logicNone"),
    [t]
  );

  const stockIndex = useMemo(() => buildStockIndex(products), [products]);
  const keyword = search.trim().toLowerCase();

  const controlRows = useMemo(() => {
    const scoped = controls.filter((control) => inDateRange(control.releaseDate, range));
    return buildControlRows(scoped, stockIndex, customerName).filter((row) => {
      if (onlyRestricted && row.controlLogic === "None") return false;
      if (!keyword) return true;
      return (
        row.itemCode.toLowerCase().includes(keyword) ||
        row.productName.toLowerCase().includes(keyword) ||
        row.stockSite.toLowerCase().includes(keyword) ||
        row.customerNames.some((name) => name.toLowerCase().includes(keyword))
      );
    });
  }, [controls, range, stockIndex, customerName, onlyRestricted, keyword]);

  const siteRows = useMemo(() => buildSiteRows(controlRows, products), [controlRows, products]);
  const customerRows = useMemo(() => {
    const rows = buildCustomerAccessRows(controlRows, customerName);
    return keyword ? rows.filter((row) => row.customerName.toLowerCase().includes(keyword) || row.products.some((p) => p.name.toLowerCase().includes(keyword))) : rows;
  }, [controlRows, customerName, keyword]);
  const uncontrolledRows = useMemo(() => {
    const rows = buildUncontrolledProducts(buildControlRows(controls, stockIndex, customerName), stockIndex);
    return keyword
      ? rows.filter((row) => row.code.toLowerCase().includes(keyword) || row.name.toLowerCase().includes(keyword) || row.site.toLowerCase().includes(keyword))
      : rows;
  }, [controls, stockIndex, customerName, keyword]);

  const kpis = useMemo(
    () => ({
      controls: controlRows.length,
      released: controlRows.reduce((sum, row) => sum + row.quantityReleased, 0),
      customer: controlRows.filter((row) => row.controlLogic === "Customer").length,
      consumption: controlRows.filter((row) => row.controlLogic === "Consumption").length,
      uncontrolled: uncontrolledRows.length,
    }),
    [controlRows, uncontrolledRows]
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportExcelWorkbook(
        `Item-Control-Report_${reportFileStamp()}`,
        [
          {
            name: t("opsReports.sheetControls"),
            columns: [
              { key: "itemCode", header: t("opsReports.productCode"), width: 18 },
              { key: "productName", header: t("opsReports.product"), width: 40 },
              { key: "stockSite", header: t("opsReports.site"), width: 12 },
              { key: "batch", header: t("opsReports.batch"), width: 16 },
              { key: "logic", header: t("opsReports.controlLogic"), width: 18 },
              { key: "quantityReleased", header: t("opsReports.qtyReleased"), type: "int", width: 14 },
              { key: "stockOnHand", header: t("opsReports.stockOnHand"), type: "int", width: 14 },
              { key: "releasedShare", header: t("opsReports.releasedShare"), type: "percent", width: 16 },
              { key: "maxQtyPerCustomer", header: t("opsReports.maxPerCustomer"), type: "int", width: 16 },
              { key: "customerCount", header: t("opsReports.authorizedCustomers"), type: "int", width: 16 },
              { key: "customers", header: t("opsReports.customer"), width: 50 },
              { key: "releaseDate", header: t("opsReports.releaseDate"), type: "date" },
              { key: "acceptedBy", header: t("opsReports.acceptedBy"), width: 20 },
              { key: "acceptedAt", header: t("opsReports.acceptedAt"), type: "datetime" },
            ],
            rows: controlRows.map((row) => ({
              ...row,
              batch: row.batchNumber || t("opsReports.allBatches"),
              logic: logicLabel(row.controlLogic),
              customerCount: row.authorizedCustomerIds.length,
              customers: row.customerNames.join(", "),
            })),
            totals: {
              itemCode: t("codReports.totalsRow"),
              quantityReleased: kpis.released,
              stockOnHand: controlRows.reduce((sum, row) => sum + row.stockOnHand, 0),
            },
          },
          {
            name: t("opsReports.sheetBySite"),
            columns: [
              { key: "site", header: t("opsReports.site"), width: 12 },
              { key: "controls", header: t("opsReports.controls"), type: "int" },
              { key: "customerRestricted", header: t("opsReports.customerRestricted"), type: "int", width: 18 },
              { key: "consumptionCapped", header: t("opsReports.consumptionCapped"), type: "int", width: 18 },
              { key: "quantityReleased", header: t("opsReports.totalReleased"), type: "int", width: 14 },
              { key: "stockOnHand", header: t("opsReports.stockOnHand"), type: "int", width: 14 },
              { key: "productsInStock", header: t("opsReports.productsInStock"), type: "int", width: 16 },
              { key: "uncontrolledInStock", header: t("opsReports.uncontrolledInStock"), type: "int", width: 18 },
            ],
            rows: siteRows as unknown as Record<string, unknown>[],
          },
          {
            name: t("opsReports.sheetCustomerAccess"),
            columns: [
              { key: "customer", header: t("opsReports.customer"), width: 40 },
              { key: "productCode", header: t("opsReports.productCode"), width: 18 },
              { key: "product", header: t("opsReports.product"), width: 40 },
              { key: "site", header: t("opsReports.site"), width: 12 },
              { key: "maxQty", header: t("opsReports.maxPerCustomer"), type: "int", width: 16 },
            ],
            rows: customerRows.flatMap((row) =>
              row.products.map((p) => ({ customer: row.customerName, productCode: p.code, product: p.name, site: p.site, maxQty: p.maxQty }))
            ),
          },
          {
            name: t("opsReports.sheetUncontrolled"),
            columns: [
              { key: "code", header: t("opsReports.productCode"), width: 18 },
              { key: "name", header: t("opsReports.product"), width: 40 },
              { key: "site", header: t("opsReports.site"), width: 12 },
              { key: "stock", header: t("opsReports.stockOnHand"), type: "int", width: 14 },
            ],
            rows: uncontrolledRows as unknown as Record<string, unknown>[],
          },
        ],
        { rtl: i18n.language?.startsWith("ar") }
      );
      showToast(t("codReports.exportSuccess"), "success");
    } catch {
      showToast(t("codReports.exportFailed"), "error");
    } finally {
      setExporting(false);
    }
  };

  const tabs = [
    { value: "controls" as const, label: t("opsReports.controls"), icon: <TuneRoundedIcon sx={{ fontSize: 18 }} />, count: controlRows.length },
    { value: "site" as const, label: t("opsReports.bySite"), icon: <WarehouseRoundedIcon sx={{ fontSize: 18 }} />, count: siteRows.length },
    { value: "customers" as const, label: t("opsReports.customerAccess"), icon: <GroupsRoundedIcon sx={{ fontSize: 18 }} />, count: customerRows.length },
    { value: "uncontrolled" as const, label: t("opsReports.uncontrolled"), icon: <ReportProblemRoundedIcon sx={{ fontSize: 18 }} />, count: uncontrolledRows.length },
    { value: "charts" as const, label: t("codReports.chartsTab"), icon: <InsertChartRoundedIcon sx={{ fontSize: 18 }} /> },
  ];

  const renderEmpty = () => <EmptyState title={t("opsReports.noData")} description={t("opsReports.noDataHint")} />;

  return (
    <Box sx={{ ...pageShellSx, maxWidth: 1440, mx: "auto" }}>
      <PageHeader title={t("opsReports.whmTitle")} subtitle={t("opsReports.whmSubtitle")} />

      <Box sx={panelSx}>
        <ReportToolbar
          range={range}
          onRangeChange={setRange}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t("opsReports.searchItems")}
          onlyBalance={onlyRestricted}
          onOnlyBalanceChange={setOnlyRestricted}
          toggleLabel={t("opsReports.onlyRestricted")}
          onExport={handleExport}
          exporting={exporting}
          exportDisabled={loading || (controlRows.length === 0 && uncontrolledRows.length === 0)}
        />

        <Box sx={{ containerType: "inline-size" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 2,
              mb: 3,
              "@container (min-width: 620px)": { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
              "@container (min-width: 980px)": { gridTemplateColumns: "repeat(5, minmax(0, 1fr))" },
            }}
          >
            <CodStatCard label={t("opsReports.controls")} value={kpis.controls} tone="ink" icon={<TuneRoundedIcon />} loading={loading} />
            <CodStatCard label={t("opsReports.totalReleased")} value={kpis.released.toLocaleString("en-US")} tone="success" icon={<Inventory2RoundedIcon />} loading={loading} />
            <CodStatCard label={t("opsReports.customerRestricted")} value={kpis.customer} tone="info" icon={<GroupsRoundedIcon />} loading={loading} />
            <CodStatCard label={t("opsReports.consumptionCapped")} value={kpis.consumption} tone="warning" icon={<DataUsageRoundedIcon />} loading={loading} />
            <CodStatCard label={t("opsReports.uncontrolledInStock")} value={kpis.uncontrolled} tone="danger" icon={<ReportProblemRoundedIcon />} loading={loading} />
          </Box>
        </Box>

        <CodTabBar value={view} tabs={tabs} onChange={setView} />

        {loading ? (
          <TableSkeleton rows={6} columns={5} />
        ) : view === "charts" ? (
          controlRows.length === 0 && uncontrolledRows.length === 0 ? renderEmpty() : (
            <WhmChartsSection
              controlRows={controlRows}
              siteRows={siteRows}
              customerRows={customerRows}
              uncontrolledRows={uncontrolledRows}
              logicLabel={logicLabel}
            />
          )
        ) : view === "controls" ? (
          controlRows.length === 0 ? renderEmpty() : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {controlRows.map((row) => {
                const tone = LOGIC_TONE[row.controlLogic];
                const share = Math.min(1, row.releasedShare);
                return (
                  <Box
                    key={row.id}
                    sx={{
                      ...rowSx,
                      gridTemplateColumns: { xs: "minmax(0, 1fr) auto", md: "minmax(0, 2fr) minmax(0, 0.9fr) minmax(0, 1.2fr) minmax(0, 0.8fr)" },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary }}>{row.productName}</Typography>
                        <Chip size="small" label={logicLabel(row.controlLogic)} sx={{ height: 22, fontWeight: 700, fontSize: 11, color: tone.color, bgcolor: tone.bg, border: `1px solid ${tone.color}` }} />
                      </Box>
                      <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, mt: 0.25 }}>
                        {row.itemCode} · {t("opsReports.site")} {row.stockSite || "—"} · {row.batchNumber || t("opsReports.allBatches")}
                        {row.releaseDate ? ` · ${formatCodDate(row.releaseDate)}` : ""}
                      </Typography>
                      {row.controlLogic === "Customer" && row.customerNames.length > 0 && (
                        <Typography sx={{ fontSize: 12, color: NJIM.textMuted, mt: 0.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.customerNames.join(", ")}>
                          {row.customerNames.slice(0, 3).join(", ")}
                          {row.customerNames.length > 3 ? ` +${row.customerNames.length - 3}` : ""}
                        </Typography>
                      )}
                    </Box>
                    <Num label={t("opsReports.qtyReleased")} value={row.quantityReleased} />
                    <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 11, color: NJIM.textSecondary, fontWeight: 600 }}>
                        {t("opsReports.stockOnHand")}: {row.stockOnHand.toLocaleString("en-US")}
                      </Typography>
                      <Box sx={{ mt: 0.75, height: 6, borderRadius: 999, bgcolor: NJIM.muted, overflow: "hidden" }}>
                        <Box sx={{ height: "100%", width: `${share * 100}%`, bgcolor: share >= 1 ? NJIM.danger : NJIM.mint, borderRadius: 999 }} />
                      </Box>
                      <Typography sx={{ mt: 0.25, fontSize: 11, color: NJIM.textMuted, fontVariantNumeric: "tabular-nums" }}>
                        {(row.releasedShare * 100).toFixed(0)}% {t("opsReports.releasedShare").toLowerCase()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: { xs: "none", md: "block" } }}>
                      {row.controlLogic === "Customer" ? (
                        <Num label={t("opsReports.authorizedCustomers")} value={row.authorizedCustomerIds.length} />
                      ) : row.maxQtyPerCustomer != null ? (
                        <Num label={t("opsReports.maxPerCustomer")} value={row.maxQtyPerCustomer} />
                      ) : (
                        <Typography sx={{ fontSize: 12, color: NJIM.textMuted, textAlign: "end" }}>—</Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )
        ) : view === "site" ? (
          siteRows.length === 0 ? renderEmpty() : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
              {siteRows.map((row) => (
                <Box key={row.key} sx={{ ...rowSx, gridTemplateColumns: "1fr", gap: 1.5, p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: "12px", display: "grid", placeItems: "center", bgcolor: NJIM.mintWash, color: NJIM.ink }}>
                      <WarehouseRoundedIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: NJIM.ink }}>{row.site}</Typography>
                      <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
                        {t("opsReports.controls")}: {row.controls} · {t("opsReports.productsInStock")}: {row.productsInStock}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                    <MiniTile label={t("opsReports.totalReleased")} value={row.quantityReleased} />
                    <MiniTile label={t("opsReports.stockOnHand")} value={row.stockOnHand} />
                    <MiniTile label={t("opsReports.customerRestricted")} value={row.customerRestricted} color={NJIM.info} />
                    <MiniTile label={t("opsReports.uncontrolledInStock")} value={row.uncontrolledInStock} color={row.uncontrolledInStock > 0 ? NJIM.danger : NJIM.textMuted} />
                  </Box>
                </Box>
              ))}
            </Box>
          )
        ) : view === "customers" ? (
          customerRows.length === 0 ? renderEmpty() : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {customerRows.map((row) => (
                <Box key={row.key} sx={{ ...rowSx, gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 2fr)" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                    <CodInitialsAvatar name={row.customerName} size={36} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.customerName}>
                        {row.customerName}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
                        {t("opsReports.productsCount")}: {row.products.length}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {row.products.map((p, index) => (
                      <Chip
                        key={`${p.code}-${p.site}-${index}`}
                        size="small"
                        label={`${p.name} · ${p.site}${p.maxQty != null ? ` · ≤${p.maxQty}` : ""}`}
                        sx={{ fontWeight: 600, bgcolor: NJIM.muted, border: `1px solid ${NJIM.border}`, maxWidth: "100%" }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )
        ) : uncontrolledRows.length === 0 ? (
          renderEmpty()
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {uncontrolledRows.slice(0, 500).map((row) => (
              <Box key={row.key} sx={{ ...rowSx, py: 1.25, gridTemplateColumns: "minmax(0, 1fr) auto auto" }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.name}>
                    {row.name || row.code}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>{row.code}</Typography>
                </Box>
                <Chip size="small" label={row.site || "—"} sx={{ fontWeight: 700, bgcolor: NJIM.mintWash }} />
                <Num label={t("opsReports.stockOnHand")} value={row.stock} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const Num: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color = NJIM.textPrimary }) => (
  <Box sx={{ textAlign: "end", minWidth: 0 }}>
    <Typography sx={{ fontSize: 11, color: NJIM.textSecondary, fontWeight: 600, whiteSpace: "nowrap" }}>{label}</Typography>
    <Typography sx={{ fontSize: 15, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>{value.toLocaleString("en-US")}</Typography>
  </Box>
);

const MiniTile: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color = NJIM.ink }) => (
  <Box sx={{ px: 1.25, py: 1, borderRadius: "10px", bgcolor: NJIM.muted, border: `1px solid ${NJIM.border}` }}>
    <Typography sx={{ fontSize: 11, color: NJIM.textSecondary, fontWeight: 600 }}>{label}</Typography>
    <Typography sx={{ fontSize: 16, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>{value.toLocaleString("en-US")}</Typography>
  </Box>
);

export default WhmReportsPage;
